/**
 * 增强版工具选择器组件
 * 
 * 功能：
 * - 支持MCP工具和Builtin工具
 * - 按类型分组展示（MCP Server、Builtin分类）
 * - 支持按组全选/取消
 * - 显示工具可用性条件
 * 
 * 适用于：StructuredAgent等需要builtin tools的节点类型
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, Server, Heart, Folder, Code, Sparkles } from 'lucide-react'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible'
import { Badge } from '@/shared/components/ui/badge'
import type { ToolListItem, BuiltinToolInfo, BuiltinToolCategory } from '@/modules/agent/types/tool'

interface EnhancedToolSelectorProps {
  mcpTools: ToolListItem[]
  builtinTools: BuiltinToolInfo[]
  selectedTools: string[]
  onToolsChange: (tools: string[]) => void
  isLoading?: boolean
  nodeType?: string // 用于显示相关性提示
}

// Builtin工具分类图标映射
const BUILTIN_CATEGORY_ICONS: Record<BuiltinToolCategory, React.ComponentType<{ className?: string }>> = {
  heartbeat: Heart,
  workspace: Folder,
  skills: Sparkles,
  code: Code,
}

// Builtin工具分类名称映射
const BUILTIN_CATEGORY_NAMES: Record<BuiltinToolCategory, string> = {
  heartbeat: '心跳工具',
  workspace: '工作区工具', 
  skills: '技能工具',
  code: '代码执行',
}

export function EnhancedToolSelector({ 
  mcpTools, 
  builtinTools, 
  selectedTools, 
  onToolsChange, 
  isLoading = false,
  nodeType: _nodeType = 'structured_agent'
}: EnhancedToolSelectorProps) {
  // 按 MCP server_name 分组MCP工具
  const groupedMcpTools = mcpTools.reduce((acc, tool) => {
    const groupName = tool.server_name || '其他工具'
    if (!acc[groupName]) {
      acc[groupName] = []
    }
    acc[groupName].push(tool)
    return acc
  }, {} as Record<string, ToolListItem[]>)

  // 按分类分组builtin工具
  const groupedBuiltinTools = builtinTools.reduce((acc, tool) => {
    if (!acc[tool.category]) {
      acc[tool.category] = []
    }
    acc[tool.category].push(tool)
    return acc
  }, {} as Record<BuiltinToolCategory, BuiltinToolInfo[]>)

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }))
  }

  const handleMcpGroupSelectAll = (groupTools: ToolListItem[], checked: boolean) => {
    const groupToolIds = groupTools.map(t => t.id)
    let newTools: string[]
    if (checked) {
      newTools = [...new Set([...selectedTools, ...groupToolIds])]
    } else {
      newTools = selectedTools.filter((id: string) => !groupToolIds.includes(id))
    }
    onToolsChange(newTools)
  }

  const handleBuiltinGroupSelectAll = (groupTools: BuiltinToolInfo[], checked: boolean) => {
    const groupToolIds = groupTools.map(t => t.name)
    let newTools: string[]
    if (checked) {
      newTools = [...new Set([...selectedTools, ...groupToolIds])]
    } else {
      newTools = selectedTools.filter((id: string) => !groupToolIds.includes(id))
    }
    onToolsChange(newTools)
  }

  const handleToolToggle = (toolId: string, checked: boolean) => {
    const newTools = checked
      ? [...selectedTools, toolId]
      : selectedTools.filter((id: string) => id !== toolId)
    onToolsChange(newTools)
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
        加载工具列表...
      </div>
    )
  }

  const totalTools = mcpTools.length + builtinTools.length
  if (totalTools === 0) {
    return (
      <p className="text-sm text-slate-500">
        暂无可用工具，请先在项目设置中配置可用工具
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {/* Builtin工具分组 */}
      {Object.entries(groupedBuiltinTools).map(([category, tools]) => {
        const CategoryIcon = BUILTIN_CATEGORY_ICONS[category as BuiltinToolCategory]
        const categoryName = BUILTIN_CATEGORY_NAMES[category as BuiltinToolCategory]
        const groupKey = `builtin-${category}`
        const isExpanded = expandedGroups[groupKey] ?? true
        const selectedCount = tools.filter(tool => selectedTools.includes(tool.name)).length
        const allSelected = selectedCount === tools.length && tools.length > 0

        return (
          <Collapsible key={groupKey} open={isExpanded} onOpenChange={() => toggleGroup(groupKey)}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-left bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <CategoryIcon className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-sm">{categoryName}</span>
                <Badge variant="secondary" className="text-xs">
                  {selectedCount}/{tools.length}
                </Badge>
                <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                  内置工具
                </Badge>
              </div>
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => handleBuiltinGroupSelectAll(tools, checked as boolean)}
                onClick={(e) => e.stopPropagation()}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-6 pr-2 pb-2">
              <div className="space-y-1">
                {tools.map((tool) => (
                  <div key={tool.name} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        checked={selectedTools.includes(tool.name)}
                        onCheckedChange={(checked) => handleToolToggle(tool.name, checked as boolean)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono">{tool.name}</span>
                          {tool.availability === 'conditional' && (
                            <Badge variant="outline" className="text-xs">
                              条件可用
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{tool.description}</p>
                        {tool.availability === 'conditional' && tool.condition && (
                          <p className="text-xs text-amber-600 mt-1">
                            📋 {tool.condition}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )
      })}

      {/* MCP工具分组 */}
      {Object.entries(groupedMcpTools).map(([serverName, tools]) => {
        const groupKey = `mcp-${serverName}`
        const isExpanded = expandedGroups[groupKey] ?? true
        const selectedCount = tools.filter(tool => selectedTools.includes(tool.id)).length
        const allSelected = selectedCount === tools.length && tools.length > 0

        return (
          <Collapsible key={groupKey} open={isExpanded} onOpenChange={() => toggleGroup(groupKey)}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-left bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors">
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Server className="h-4 w-4 text-slate-600" />
                <span className="font-medium text-sm">{serverName}</span>
                <Badge variant="secondary" className="text-xs">
                  {selectedCount}/{tools.length}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  MCP工具
                </Badge>
              </div>
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => handleMcpGroupSelectAll(tools, checked as boolean)}
                onClick={(e) => e.stopPropagation()}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-6 pr-2 pb-2">
              <div className="space-y-1">
                {tools.map((tool) => (
                  <div key={tool.id} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        checked={selectedTools.includes(tool.id)}
                        onCheckedChange={(checked) => handleToolToggle(tool.id, checked as boolean)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono">{tool.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {tool.category}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{tool.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )
      })}

      {/* 统计信息 */}
      <div className="pt-2 border-t text-xs text-slate-500">
        已选择 {selectedTools.length} 个工具 
        (MCP: {mcpTools.filter(t => selectedTools.includes(t.id)).length}, 
        内置: {builtinTools.filter(t => selectedTools.includes(t.name)).length})
      </div>
    </div>
  )
}